/**
 * Dialog components for the Analyse page.
 * Contains: CreateCategoryDialog, CreateDashboardDialog, AddIndicatorDialog, EditIndicatorDialog, DeleteIndicatorDialog
 */

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2, FolderOpen } from 'lucide-react';
import type { AnalyseCategory, DashboardIndicator } from '../../lib/api/barratt';
import { CHART_TYPES } from './AnalyseCharts';

// --- Create Category Dialog ---
interface CreateCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newCategory: { name: string; hint: string; sort_order: number };
  setNewCategory: (cat: { name: string; hint: string; sort_order: number }) => void;
  saving: boolean;
  onCreate: () => void;
}

export function CreateCategoryDialog({ open, onOpenChange, newCategory, setNewCategory, saving, onCreate }: CreateCategoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Category</DialogTitle>
          <DialogDescription>Create a new category to organize your dashboards.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name *</label>
            <Input placeholder="Category name" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea placeholder="Description (optional)" value={newCategory.hint} onChange={(e) => setNewCategory({ ...newCategory, hint: e.target.value })} rows={2} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Sort Order</label>
            <Input type="number" placeholder="Sort order" value={newCategory.sort_order || ''} onChange={(e) => setNewCategory({ ...newCategory, sort_order: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onCreate} disabled={!newCategory.name.trim() || saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Create Dashboard Dialog ---
interface CreateDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newDashboard: { name: string; description: string; category: number; is_enabled: boolean; shared_with: 'private' | 'company' | 'company_and_suppliers' };
  setNewDashboard: (d: { name: string; description: string; category: number; is_enabled: boolean; shared_with: 'private' | 'company' | 'company_and_suppliers' }) => void;
  categories: AnalyseCategory[];
  saving: boolean;
  onCreate: () => void;
}

export function CreateDashboardDialog({ open, onOpenChange, newDashboard, setNewDashboard, categories, saving, onCreate }: CreateDashboardDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Dashboard</DialogTitle>
          <DialogDescription>Create a new dashboard to display indicators and charts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Category *</label>
            <Select value={newDashboard.category.toString()} onValueChange={(value) => setNewDashboard({ ...newDashboard, category: parseInt(value) })}>
              <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    <div className="flex items-center gap-2"><FolderOpen className="h-4 w-4" />{category.name}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categories.length === 0 && (<p className="text-xs text-muted-foreground">No categories available. Create a category first.</p>)}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Name *</label>
            <Input placeholder="Dashboard name" value={newDashboard.name} onChange={(e) => setNewDashboard({ ...newDashboard, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea placeholder="Description (optional)" value={newDashboard.description} onChange={(e) => setNewDashboard({ ...newDashboard, description: e.target.value })} rows={2} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Sharing</label>
            <Select value={newDashboard.shared_with} onValueChange={(value: 'private' | 'company' | 'company_and_suppliers') => setNewDashboard({ ...newDashboard, shared_with: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="company_and_suppliers">Shared with Suppliers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onCreate} disabled={!newDashboard.name.trim() || !newDashboard.category || saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Add/Edit Indicator Dialog ---
interface IndicatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  indicator: { name: string; chart_type: DashboardIndicator['chart_type']; hint: string };
  setIndicator: (ind: { name: string; chart_type: DashboardIndicator['chart_type']; hint: string }) => void;
  saving: boolean;
  onSave: () => void;
  saveLabel: string;
}

export function IndicatorDialog({ open, onOpenChange, title, description, indicator, setIndicator, saving, onSave, saveLabel }: IndicatorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name *</label>
            <Input placeholder="Indicator name" value={indicator.name} onChange={(e) => setIndicator({ ...indicator, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Chart Type *</label>
            <div className="grid grid-cols-3 gap-3">
              {CHART_TYPES.map((type) => (
                <div
                  key={type.value}
                  className={`p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors ${
                    indicator.chart_type === type.value ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setIndicator({ ...indicator, chart_type: type.value as DashboardIndicator['chart_type'] })}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {type.icon}
                    <span className="font-medium text-sm">{type.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea placeholder="Description (optional)" value={indicator.hint} onChange={(e) => setIndicator({ ...indicator, hint: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={!indicator.name.trim() || saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Delete Indicator Confirmation ---
interface DeleteIndicatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  indicatorName: string;
  onDelete: () => void;
  onCancel: () => void;
}

export function DeleteIndicatorDialog({ open, onOpenChange, indicatorName, onDelete, onCancel }: DeleteIndicatorDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Indicator</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{indicatorName}"? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
